import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../../components/AppIcon';
import Input from '../../../../components/ui/Input';
import api from '../../../../services/api';

const CountrySelector = ({ selectedCountry, onCountrySelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Popular countries for prioritized display
    const popularCountryCodes = ['US', 'CA', 'GB', 'AU', 'DE', 'FR', 'ES', 'IT', 'JP', 'IN', 'BR', 'MX'];

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setLoading(true);
                const response = await api.get('/core/locale/countries/');

                if (response.data.success && response.data.data.countries) {
                    const countriesData = response.data.data.countries;

                    // Sort countries: popular ones first, then alphabetically
                    const sortedCountries = countriesData.sort((a, b) => {
                        const aIndex = popularCountryCodes.indexOf(a.code);
                        const bIndex = popularCountryCodes.indexOf(b.code);

                        if (aIndex !== -1 && bIndex !== -1) {
                            return aIndex - bIndex;
                        }
                        if (aIndex !== -1) return -1;
                        if (bIndex !== -1) return 1;
                        return a.name.localeCompare(b.name);
                    });

                    setCountries(sortedCountries);
                }
            } catch (err) {
                console.error('Error fetching countries:', err);
                setError('Failed to load countries');
            } finally {
                setLoading(false);
            }
        };

        fetchCountries();
    }, []);

    const filteredCountries = useMemo(() => {
        if (!searchTerm) return countries;

        return countries.filter(country =>
            country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            country.code.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [countries, searchTerm]);

    const popularCountries = useMemo(() =>
        countries.filter(country => popularCountryCodes.includes(country.code)),
        [countries]
    );

    const handleCountrySelect = (country) => {
        onCountrySelect(country);
        setIsOpen(false);
        setSearchTerm('');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-4 border border-input rounded-lg">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                <span className="ml-2 text-muted-foreground">Loading countries...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center p-4 border border-error rounded-lg">
                <Icon name="AlertCircle" size={16} className="text-error mr-2" />
                <span className="text-error text-sm">{error}</span>
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Selected Country Display / Dropdown Trigger */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between p-3 border border-input rounded-lg cursor-pointer hover:border-primary-300 transition-colors bg-white"
            >
                {selectedCountry ? (
                    <div className="flex items-center space-x-3">
                        <span className="text-xl">{selectedCountry.flag}</span>
                        <span className="font-medium text-accent-800">{selectedCountry.name}</span>
                    </div>
                ) : (
                    <span className="text-muted-foreground">Select your country</span>
                )}
                <Icon
                    name="ChevronDown"
                    size={20}
                    className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-input rounded-lg shadow-lg z-50 max-h-80 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-3 border-b border-input">
                        <div className="relative">
                            <Icon
                                name="Search"
                                size={18}
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                            />
                            <Input
                                type="text"
                                placeholder="Search countries..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto max-h-64">
                        {/* Popular Countries */}
                        {!searchTerm && popularCountries.length > 0 && (
                            <div className="p-2">
                                <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">
                                    Popular Countries
                                </div>
                                {popularCountries.map((country) => (
                                    <button
                                        key={country.code}
                                        onClick={() => handleCountrySelect(country)}
                                        className="w-full flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-surface transition-colors text-left"
                                    >
                                        <span className="text-lg">{country.flag}</span>
                                        <span className="font-medium">{country.name}</span>
                                        {selectedCountry?.code === country.code && (
                                            <Icon name="Check" size={16} className="ml-auto text-primary" />
                                        )}
                                    </button>
                                ))}
                                <div className="border-t border-input my-2"></div>
                            </div>
                        )}

                        {/* All Countries */}
                        <div className="p-2">
                            {!searchTerm && (
                                <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">
                                    All Countries
                                </div>
                            )}
                            {filteredCountries.map((country) => (
                                <button
                                    key={country.code}
                                    onClick={() => handleCountrySelect(country)}
                                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-surface transition-colors text-left"
                                >
                                    <span className="text-lg">{country.flag}</span>
                                    <span className="font-medium">{country.name}</span>
                                    {selectedCountry?.code === country.code && (
                                        <Icon name="Check" size={16} className="ml-auto text-primary" />
                                    )}
                                </button>
                            ))}

                            {filteredCountries.length === 0 && (
                                <div className="px-3 py-4 text-center text-muted-foreground">
                                    No countries found matching "{searchTerm}"
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

export default CountrySelector; 